import { Controller, Control, FieldValues, Path } from "react-hook-form";
import ErrorsMessage from "app/components/common/errors/ErrorsMessage";
import { LiaQuestionCircle } from "react-icons/lia";
import { Tooltip } from "react-tooltip";

interface InputFieldProps<T extends FieldValues> {
  label: string;
  name: Path<T>;
  control: Control<T>;
  type: "text" | "password" | "email" | "number" | "tel" | "date";
  placeholder?: string;
  error?: string;
  required?: boolean;
  className?: string;
  rules?: Record<string, any>;
  tooltip?: string;
  errorHide?: boolean;
  removePosition?: boolean;
  disabled?: boolean;
}

function InputField<T extends FieldValues>({
  label,
  name,
  control,
  type,
  placeholder,
  error,
  required = false,
  className,
  rules = {},
  tooltip,
  errorHide = true,
  removePosition = false,
  disabled = false,
}: InputFieldProps<T>) {
  const formattedPlaceholder = placeholder
    ? placeholder.charAt(0).toUpperCase() + placeholder.slice(1).toLowerCase()
    : "";

  return (
    <div className={`text-left ${className} relative`}>
      <label
        htmlFor={name}
        className=" font-Mulish text-[16px] sm:text-base font-normal text-lightBlue mb-1  flex items-center  "
      >
        {label === "ABN"
          ? label
          : label.charAt(0).toUpperCase() + label.slice(1).toLowerCase()}
        {required && <span className="text-red-500">*</span>}

        {tooltip && (
          <>
            <LiaQuestionCircle
              data-tooltip-id={`tooltip-${name}`}
              className="text-[#D1A960] cursor-pointer text-[20px]"
            />
            <Tooltip
              id={`tooltip-${name}`}
              place="right"
              className="font-Mulish"
              style={{
                backgroundColor: "white",
                color: "#374151",
                fontSize: "14px",
                padding: "8px 12px",
                borderRadius: "8px",
                boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
                border: "1px solid #E5E7EB",
                maxWidth: "250px",
                zIndex: 999,
              }}
            >
              {tooltip}
            </Tooltip>
          </>
        )}
      </label>
      <Controller
        name={name}
        control={control}
        rules={{
          required: required ? `${label} is required` : false,
          ...rules,
        }}
        render={({ field }) => (
          <input
            {...field}
            type={type}
            min={type === "number" ? 0 : undefined}
            step={type === "number" ? "any" : undefined}
            disabled={disabled}
            placeholder={formattedPlaceholder}
            className={`w-full px-4 py-4 border-2 font-Mulish rounded-[10px] focus:outline-none placeholder-gray-400 
    ${disabled ? "bg-gray-100 text-gray-500 cursor-not-allowed" : ""}
    ${error ? "border-red-500" : "border-[#EAECF0]"}`}
            onFocus={() => {
              if (field.value === "0" || field.value === 0) {
                field.onChange("");
              }
            }}
            onKeyDown={(e) => {
              // if (
              //   type === "number" &&
              //   (e.key === "-" || e.key === "e" || e.key === ".")
              // ) {
              //   e.preventDefault();
              // }
              if (type === "number" && (e.key === "-" || e.key === "e")) {
                e.preventDefault();
              }
            }}
          />
        )}
      />

      {errorHide && error && (
        <ErrorsMessage
          title={
            error
              ?.replace(/[^\w\s]/gi, "") // Remove special characters
              .toLowerCase()
              .replace(/^\w/, (c) => c.toUpperCase()) ?? // Capitalize only first letter
            ""
          }
          className={`text-right ${
            removePosition ? "" : "absolute"
          }  top-[100%] right-0`}
        />
      )}
      {!errorHide && (
        <ErrorsMessage
          title={
            <div
              className={`absolute text-[#EA4335] text-[12px] font-normal  font-Mulish right-0 ${
                error ? "opacity-2" : "opacity-0"
              }`}
            >
              {error ? error : "testing"}
            </div>
          }
          className={`text-right ${
            removePosition ? "" : "absolute"
          }  top-[100%] right-0`}
        />
      )}
    </div>
  );
}

export default InputField;
